import { useQueries } from '@tanstack/react-query';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from 'components/pages/ProfitPoolOverview/apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useContext, useEffect, useState } from 'react';
import { useProfitPoolEvolutionContext } from '../context/ProfitPoolEvolutionContext';
import { isEmpty, sortBy } from 'lodash';
import { QueryObserverResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getPeriodLabel } from '../utils/ppevolutionUtils';

const getEvolutionData = (
    currentYearResult: any,
    yearAgoResult: any,
    selectedPPEvolutionUnit: string,
    profitPoolTierList: any[],
    evolutionFrequency: string,
) => {
    const finalData = [];
    let minProfit = Number.POSITIVE_INFINITY;
    let maxProfit = Number.NEGATIVE_INFINITY;

    const yearAgoMap = new Map();
    yearAgoResult?.forEach((item: any) => {
        yearAgoMap.set(`${item.period}-${item.tier}`, item);
    });

    for (let i = 0; i < currentYearResult?.length; i++) {
        const currentYearItem = currentYearResult[i];
        const previousYearItem = yearAgoResult[i];
        const currentYearProfit = currentYearItem?.kpi_values?.[selectedPPEvolutionUnit];
        const previousYearProfit = previousYearItem?.kpi_values?.[selectedPPEvolutionUnit] || 0;

        const periodLabel = getPeriodLabel(currentYearItem?.period);

        if (currentYearProfit < minProfit) minProfit = currentYearProfit;
        if (currentYearProfit > maxProfit) maxProfit = currentYearProfit;
        if (previousYearProfit < minProfit) minProfit = previousYearProfit;
        if (previousYearProfit > maxProfit) maxProfit = previousYearProfit;
        finalData.push({
            period: currentYearItem?.period,
            periodLabel: evolutionFrequency === 'M' ? periodLabel : currentYearItem?.period,
            data_gran: 'year_ago',
            tierReference: `${previousYearItem?.tier || currentYearItem?.tier} (YA)`,
            tier: `${
                profitPoolTierList.find(
                    (_tier) => _tier.tierReference === (previousYearItem?.tier || currentYearItem?.tier),
                )?.tierName
            } ${'(YA)'}`,
            profit_value: previousYearProfit,
        });

        finalData.push({
            period: currentYearItem?.period,
            periodLabel: evolutionFrequency === 'M' ? periodLabel : currentYearItem?.period,
            data_gran: 'current_year',
            tierReference: currentYearItem?.tier,
            tier: `${profitPoolTierList.find((_tier) => _tier.tierReference === currentYearItem?.tier)?.tierName}`,
            profit_value: currentYearProfit,
        });
    }

    return { finalData, minProfit, maxProfit };
};

const useProfitPoolEvolution = (selectedPPEvolutionUnit: string) => {
    const {
        idForRestOfComponent,
        multiSelectedTiers,
        profitPoolTierList,
        timePeriodSelection,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);
    const { evolutionFrequency } = useProfitPoolEvolutionContext();
    const [offset, setOffset] = useState(0);
    const [allData, setAllData] = useState<any[]>([]);
    const [min, setMin] = useState<number>(0);
    const [max, setMax] = useState<number>(0);

    const fetchNextPage = () => {
        setOffset((prevOffset) => prevOffset + PROFIT_POOL_API_LIMIT);
    };

    const {
        data: newData,
        isPending,
        isError,
        isSuccess,
    } = useQueries({
        queries: [true, false].map((_yearAgo: boolean) => {
            return {
                queryKey: [
                    'profit-pool-evolution',
                    evolutionFrequency,
                    selectedPPEvolutionUnit,
                    multiSelectedTiers,
                    offset,
                    _yearAgo,
                    idForRestOfComponent,
                    timePeriodSelection,
                ],
                queryFn: ({ signal }) =>
                    fetchProfitPoolData({
                        selectedSubFilter: { dataKey: '', dimension: '' },
                        kpi: selectedPPEvolutionUnit,
                        tierList: multiSelectedTiers,
                        yearAgo: _yearAgo,
                        groupedByList: ['period'],
                        dimIdsFilter: idForRestOfComponent,
                        frequency: evolutionFrequency,
                        timePeriod: JSON.parse(timePeriodSelection),
                        signal,
                    }),
                enabled: Boolean(
                    allDimAndAttributeTypeList &&
                        'loading' in allDimAndAttributeTypeList &&
                        !timePeriodLoading &&
                        timePeriodSelection,
                ),
            };
        }),
        combine: (results: QueryObserverResult<any, Error>[]) => {
            return {
                data: results.map((result) => result.data?.data),
                isPending: results.some((result) => result.isPending),
                isError: results.some((result) => result.isError),
                isSuccess: results.every((result) => result.isSuccess),
            };
        },
    });

    useEffect(() => {
        if (isSuccess && newData) {
            const [yearAgoResult, currentYearResult] = newData.map((_data) => {
                return sortBy(_data, 'period');
            });
            let minProfit = Number.POSITIVE_INFINITY;
            let maxProfit = Number.NEGATIVE_INFINITY;
            if (offset === 0) {
                const { finalData, minProfit, maxProfit } = getEvolutionData(
                    currentYearResult,
                    yearAgoResult,
                    selectedPPEvolutionUnit,
                    profitPoolTierList,
                    evolutionFrequency,
                );
                setAllData(finalData);
                setMin(minProfit === Number.POSITIVE_INFINITY ? 0 : minProfit);
                setMax(maxProfit === Number.NEGATIVE_INFINITY ? 0 : maxProfit);
            } else {
                // Merge new data with existing data when offset > 0
                const { finalData, minProfit, maxProfit } = getEvolutionData(
                    currentYearResult,
                    yearAgoResult,
                    selectedPPEvolutionUnit,
                    profitPoolTierList,
                    evolutionFrequency,
                );
                setAllData((prevData) => {
                    const mergedData = [...prevData, ...finalData];
                    return mergedData;
                });
                setMin(minProfit === Number.POSITIVE_INFINITY ? 0 : minProfit);
                setMax(maxProfit === Number.NEGATIVE_INFINITY ? 0 : maxProfit);
            }
        } else if (isEmpty(newData) && isEmpty(allData)) {
            setAllData([]);
            setMin(0);
            setMax(0);
        }
    }, [newData, isSuccess, offset]);

    return {
        data: allData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        min,
        max,
    };
};

export default useProfitPoolEvolution;
